import { ProviderProfilePage } from "@/features/profile/components/ProviderProfilePage";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import {
  RecordLoadFailed,
  RecordNotInBusinessUnit,
} from "@/components/page-message";
import { useOrgPath } from "@/hooks/useOrgPath";
import {
  isRecordUnavailableError,
  retryUnlessRecordUnavailable,
} from "@/common/utils/record-unavailable-error";
import { useTRPC } from "@/trpc";
import Loading from "@/components/Loading";
import { useProviderProfileData } from "@/features/profile/hooks/useProviderProfileData";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { ProviderOrganizationWageSetsSection } from "@/features/organization-wage-sets/components/provider-organization-wage-sets-section";
import { Permission, useRolePermissions } from "@/hooks/use-role-permissions";

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/provider-profile/$userId/",
)({
  errorComponent: ProviderProfileError,
  component: OrgProviderProfileRoute,
});

function ProviderProfileError({ error, reset }: ErrorComponentProps) {
  const orgPath = useOrgPath();
  if (isRecordUnavailableError(error)) {
    return (
      <RecordNotInBusinessUnit
        title="Provider not found"
        recordLabel="provider"
        back={{ to: orgPath("/providers/all"), label: "Back to providers" }}
      />
    );
  }
  return <RecordLoadFailed recordLabel="profile" onRetry={reset} />;
}

function OrgProviderProfileRoute() {
  const { userId, organizationBusinessUnitId } = Route.useParams();
  const { user } = Route.useRouteContext();
  const { hasPermission } = useRolePermissions(user);

  const trpc = useTRPC();
  const queryClient = useQueryClient();

  // Intentionally scoped to the selected BU only — cross-BU hired badge is out of scope.
  // Affiliation status is visible to any BU member; no view permission gate.
  const affiliationsQuery = useQuery(
    trpc.organizationBusinessUnitAffiliation.listForBusinessUnit.queryOptions(
      {
        organizationBusinessUnitId,
      },
      {
        enabled: Boolean(organizationBusinessUnitId),
      },
    ),
  );

  const hiredAffiliation = affiliationsQuery.data?.find(
    (a) => a.userId === userId,
  );
  const providerUserQuery = useQuery(
    trpc.profile.getSharedUser.queryOptions(
      {
        userId,
      },
      { retry: retryUnlessRecordUnavailable },
    ),
  );
  const profileData = useProviderProfileData(
    userId,
    organizationBusinessUnitId,
  );
  const credentialEditAccessQuery = useQuery(
    trpc.credentialFiles.getCredentialEditAccess.queryOptions(
      {
        userId,
        organizationBusinessUnitId,
      },
      { retry: retryUnlessRecordUnavailable },
    ),
  );
  const requestCredentialEditAccessMutation = useMutation(
    trpc.credentialFiles.requestCredentialEditAccess.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.credentialFiles.getCredentialEditAccess.queryOptions({
            userId,
            organizationBusinessUnitId,
          }),
        );
        toast.success("Credential edit request sent");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to send request");
      },
    }),
  );

  if (providerUserQuery.error) {
    throw providerUserQuery.error;
  }

  if (profileData.error) {
    throw profileData.error;
  }

  if (credentialEditAccessQuery.error) {
    throw credentialEditAccessQuery.error;
  }

  if (
    providerUserQuery.isLoading ||
    !providerUserQuery.data ||
    profileData.isLoading ||
    !profileData.data ||
    credentialEditAccessQuery.isLoading
  ) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loading className="h-16 w-16" />
      </div>
    );
  }

  return (
    <div>
      {hiredAffiliation && (
        <div className="bg-white px-4 pt-4 pb-0 md:px-8 flex items-center gap-2">
          <Badge variant="default" className="gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Hired
          </Badge>
        </div>
      )}
      {hiredAffiliation && hasPermission(Permission.ViewJobOffers) && (
        <ProviderOrganizationWageSetsSection
          organizationBusinessUnitId={organizationBusinessUnitId}
          providerUserId={userId}
        />
      )}
      <ProviderProfilePage
        user={providerUserQuery.data}
        credentials={profileData.data.credentials}
        jobPreferences={profileData.data.jobPreferences}
        sharePermissions={profileData.data.sharePermissions}
        allowDocumentUploads={Boolean(
          credentialEditAccessQuery.data?.canEdit &&
            credentialEditAccessQuery.data.request?.status === "approved",
        )}
        credentialEditRequest={credentialEditAccessQuery.data?.request ?? null}
        isRequestingCredentialEditAccess={
          requestCredentialEditAccessMutation.isPending
        }
        onRequestCredentialEditAccess={(message) =>
          requestCredentialEditAccessMutation.mutate({
            providerUserId: userId,
            organizationBusinessUnitId,
            message,
          })
        }
        organizationBusinessUnitId={organizationBusinessUnitId}
      />
    </div>
  );
}
